/**
 * The five hits most recently opened from the palette.
 *
 * The one rule that governs this whole file: **no money is ever persisted**.
 * A hit carries a badge drawn from the exposure projection; a rupee figure
 * restored from localStorage the next morning is not a ledger projection, it is
 * a screenshot of one, and this console does not render those. The badge and the
 * `meta` line are stripped on the way in, so there is nothing to leak back out
 * even if a later version starts rendering the stored shape more richly.
 *
 * Storage is best-effort throughout. Private-mode Safari throws on `setItem`,
 * an operator can clear site data mid-session, and a quota error while opening a
 * search result must never break navigating to it.
 */

import type { IconName } from '@w7/ui'
import type { SearchHit, SearchKind } from '@/data/api'

const KEY = 'w7.console.recentSearch'
const LIMIT = 5

/** Exactly the fields a recent row needs to render and route. Nothing else. */
export interface RecentHit {
  id: string
  kind: SearchKind
  title: string
  subtitle: string
  to: string
  icon: IconName
}

const KINDS: SearchKind[] = ['account', 'market', 'transfer', 'action']

/**
 * Validate on the way OUT, not just on the way in.
 *
 * The store is a string an operator (or a stale build) can put anything into;
 * a row missing `to` would render as a dead option, which is worse than a
 * missing history.
 */
function parse(raw: string | null): RecentHit[] {
  if (!raw) return []
  try {
    const value: unknown = JSON.parse(raw)
    if (!Array.isArray(value)) return []
    return value
      .filter((row): row is RecentHit => {
        if (!row || typeof row !== 'object') return false
        const r = row as Partial<RecentHit>
        return (
          typeof r.id === 'string' &&
          typeof r.title === 'string' &&
          typeof r.to === 'string' &&
          r.to.startsWith('/') &&
          typeof r.icon === 'string' &&
          typeof r.kind === 'string' &&
          KINDS.includes(r.kind)
        )
      })
      .slice(0, LIMIT)
      .map((row) => ({
        id: row.id,
        kind: row.kind,
        title: row.title,
        subtitle: typeof row.subtitle === 'string' ? row.subtitle : '',
        to: row.to,
        icon: row.icon,
      }))
  } catch {
    return []
  }
}

export function readRecents(): RecentHit[] {
  try {
    return parse(window.localStorage.getItem(KEY))
  } catch {
    return []
  }
}

/** The projection-free projection of a hit. The only shape that reaches disk. */
export function toRecent(hit: Pick<SearchHit, 'id' | 'kind' | 'title' | 'subtitle' | 'to' | 'icon'>): RecentHit {
  return {
    id: hit.id,
    kind: hit.kind,
    title: hit.title,
    subtitle: hit.subtitle,
    to: hit.to,
    icon: hit.icon,
  }
}

/**
 * Push a hit to the front, deduped by id, capped at five.
 *
 * Returns the new list so a caller can render it without a second read — the
 * palette closes on select, so there is no re-read to hang the update on.
 */
export function pushRecent(hit: Parameters<typeof toRecent>[0]): RecentHit[] {
  const row = toRecent(hit)
  const next = [row, ...readRecents().filter((r) => r.id !== row.id)].slice(0, LIMIT)
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    // Quota or a locked-down storage partition. The in-memory list the caller
    // just received is still correct for this session.
  }
  return next
}

export function clearRecents(): void {
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    /* nothing to do — there is no state to repair */
  }
}

/** For tests and for the "clear history" affordance, if one is ever added. */
export const RECENTS_KEY = KEY
export const RECENTS_LIMIT = LIMIT
